/* Progressive enhancement for the public header's native disclosures. */
if (!customElements.get('yatdim-header')) {
  customElements.define('yatdim-header', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.menu = this.querySelector('.yd-header__menu');
      this.disclosures = [...this.querySelectorAll('.yd-nav__disclosure')];
      this.mobile = window.matchMedia('(max-width: 1199px)');
      this.addEventListener('keydown', (event) => {
        if (event.key === ' ' && this.openCart(event)) return;
        if (event.key !== 'Escape') return;
        const disclosure = event.target.closest('details[open]');
        if (!disclosure || !this.contains(disclosure)) return;
        event.preventDefault();
        event.stopPropagation();
        disclosure.open = false;
        disclosure.querySelector('summary')?.focus();
      }, options);
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.closeMenus();
      }, options);
      this.addEventListener('focusout', (event) => {
        if (!event.relatedTarget) return;
        for (const disclosure of this.disclosures) {
          if (disclosure.open && !disclosure.contains(event.relatedTarget)) disclosure.open = false;
        }
        if (!this.contains(event.relatedTarget)) this.closeMenus();
      }, options);
      this.addEventListener('click', (event) => {
        this.openCart(event);
        if (event.target.closest('a')) this.closeMenus();
      }, options);
      this.addEventListener('toggle', (event) => {
        const current = event.target;
        if (!current.open || !current.classList.contains('yd-nav__disclosure')) return;
        for (const disclosure of this.disclosures) {
          if (disclosure !== current && !disclosure.contains(current) && !current.contains(disclosure)) disclosure.open = false;
        }
        this.positionPanel(current);
      }, { ...options, capture: true });
      this.mobile.addEventListener('change', () => {
        const focused = document.activeElement;
        this.closeMenus();
        if (this.contains(focused) && !focused.getClientRects().length) {
          (this.mobile.matches ? this.menu?.querySelector('summary') : this.querySelector('.yd-header__logo'))?.focus();
        }
        this.updateHeight();
      }, options);
      window.addEventListener('resize', () => this.scheduleHeight(), options);
      window.addEventListener('scroll', () => this.scheduleHeight(), { ...options, passive: true });
      document.addEventListener('DOMContentLoaded', () => this.updateHeight(), options);
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.updateHeight());
        this.observer.observe(this);
        for (const section of document.querySelectorAll('.shopify-section-group-header-group')) this.observer.observe(section);
      }
      this.updateHeight();
    }

    /** Maintain the theme offsets consumed by product, filters and cart panels. */
    updateHeight() {
      const root = document.documentElement.style;
      const sticky = this.classList.contains('yd-header--sticky');
      const menuTop = Math.max(0, this.getBoundingClientRect().bottom);
      const end = sticky ? menuTop : 0;
      const paddedEnd = end + (window.matchMedia('(min-width: 769px)').matches ? 56 : 20);
      root.setProperty('--yd-sticky-height', `${sticky ? this.offsetHeight : 0}px`);
      root.setProperty('--yd-menu-top', `${menuTop}px`);
      root.setProperty('--header-height', `${this.offsetHeight}px`);
      root.setProperty('--header-end', `${end}px`);
      root.setProperty('--header-end-padded', `${paddedEnd}px`);
      const content = document.getElementById('main-content');
      if (content) root.setProperty('--content-start', `${content.getBoundingClientRect().top + window.scrollY}px`);
    }

    scheduleHeight() {
      if (this.heightFrame) return;
      this.heightFrame = requestAnimationFrame(() => {
        this.heightFrame = null;
        this.updateHeight();
      });
    }

    positionPanel(disclosure) {
      const panel = disclosure.querySelector(':scope > .yd-nav__panel');
      if (!panel || this.mobile.matches || disclosure.closest('.yd-nav__panel')) return;
      panel.classList.remove('yd-nav__panel--align-end');
      const bounds = panel.getBoundingClientRect();
      const rtl = getComputedStyle(this).direction === 'rtl';
      if (rtl ? bounds.left < 20 : bounds.right > window.innerWidth - 20) panel.classList.add('yd-nav__panel--align-end');
    }

    /** Also works after the theme editor replaces only the header section. */
    openCart(event) {
      if (event.defaultPrevented || !event.target.closest('.yd-header__cart')) return false;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return false;
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: event.target.closest('.yd-header__cart') } }));
      return true;
    }

    closeMenus() {
      if (this.menu) this.menu.open = false;
      for (const disclosure of this.disclosures) disclosure.open = false;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.heightFrame);
      for (const name of ['--yd-sticky-height', '--yd-menu-top', '--header-height', '--header-end', '--header-end-padded', '--content-start']) document.documentElement.style.removeProperty(name);
    }
  });
}
